import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { csrf, login as apiLogin, verify2FA, logout as apiLogout } from '@features/auth/api';
import {
  authKeys,
  useAuthSession,
  announceAuthChange,
  resetAuthSession,
  AUTH_CHANNEL,
} from '@features/auth/queries';

// Exported so tests can wrap components in a synthetic <AuthContext.Provider>
// with a fake user, without spinning up the full AuthProvider (which fires
// network calls). Not for production use — real code should read the auth
// context via useAuth().
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/**
 * True when the authenticated user carries `ability` via any of their
 * assigned roles. Falls back to false for anon / mid-load / missing
 * ability. Import the ability name from `@shared/constants/permissions`
 * (PERM.X) rather than hardcoding — the string must match the Spatie
 * seeder byte-for-byte and the constants file is the single source.
 *
 * FE checks are UX only; every write path re-checks server-side.
 */
export const useCan = (ability) => {
  const auth = useAuth();
  if (!ability) return false;
  const user = auth?.user;
  if (!user) return false;

  // Role-derived grants first — the common case, avoids scanning the
  // direct-grant array when a role already covers it.
  const viaRole = user.roles?.some((r) =>
    r?.permissions?.some((p) => p?.name === ability),
  );
  if (viaRole) return true;

  // Direct-to-user grants (Spatie's $user->givePermissionTo(...)).
  // The `/api/auth/status` endpoint serializes these as `user.permissions`,
  // kept distinct from role-derived so the admin UI can render them
  // separately. See docs/authorization/direct-permissions.md.
  return !!user.permissions?.some((p) => p?.name === ability);
};

export default function AuthProvider({ children }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // Identity is server state — it lives in the query cache so a sibling tab
  // can invalidate it and window focus can revalidate it. Everything else in
  // this provider is genuinely local: form fields and request status.
  const { data: user = null, isPending: checking } = useAuthSession();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTwoFactorRequired, setIsTwoFactorRequired] = useState(false);

  // Follow session changes made in OTHER tabs of this browser.
  //
  // The cache is cleared, not just the auth key: if a different user signed in
  // next door, every cached list and detail in this tab belongs to the person
  // who is no longer logged in. Discipline 6 in
  // docs/frontend/server-state-migration.md — clear on logout AND user switch.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;

    const channel = new BroadcastChannel(AUTH_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data?.type !== 'auth-changed') return;
      resetAuthSession(queryClient);
    };

    return () => channel.close();
  }, [queryClient]);

  /**
   * Take on an identity the server just handed us.
   *
   * Written straight into the cache instead of refetching — the login and 2FA
   * responses already carry the user with its roles and permissions, so a
   * second round trip would only confirm what we hold. The cache is cleared
   * first because signing in as someone new must not inherit the previous
   * occupant's cached lists.
   */
  const adoptSession = (nextUser) => {
    queryClient.clear();
    queryClient.setQueryData(authKeys.session(), nextUser ?? null);
    announceAuthChange();
  };

  const signIn = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null); setLoading(true);
    try {
      // Get CSRF token first
      await csrf();
      
      // Small delay to ensure cookie is set
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const response = await apiLogin(email, password);
      const data = response.data;

      if (data?.two_factor_required) {
        setIsTwoFactorRequired(true);
        return;
      }
      
      // Use user data returned from login instead of making separate API call
      if (data?.user) {
        adoptSession(data.user);
        navigate('/user-home', { replace: true });
      } else {
        // Fallback to /auth/status if login didn't echo user data.
        // Login just succeeded, so we expect authenticated=true here —
        // if not, something's badly wrong on the backend.
        await queryClient.refetchQueries({ queryKey: authKeys.session() });
        if (!queryClient.getQueryData(authKeys.session())) {
          throw new Error('Session not established after login.');
        }
        announceAuthChange();
        navigate('/user-home', { replace: true });
      }
    } catch (err) {
      // console.log('Login error:', err);
      // console.log('Error response:', err.response);
      // console.log('Error response data:', err.response?.data);
      // console.log('Error status:', err.response?.status);
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitTwoFactor = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null); setLoading(true);
    try {
      const response = await verify2FA(twoFactorCode);
      const data = response.data;
      
      // Use user data returned from 2FA verification instead of making separate API call
      if (data?.user) {
        adoptSession(data.user);
        // Reset the 2FA-required flag on success. Without this, if the
        // user later logs out in the same tab, /login re-renders the
        // 2FA form (because SignInCard branches on isTwoFactorRequired)
        // — because in-memory provider state outlives the auth session.
        setIsTwoFactorRequired(false);
        setTwoFactorCode('');
        navigate('/user-home', { replace: true });
      } else {
        // Fallback to /auth/status if 2FA didn't echo user data.
        await queryClient.refetchQueries({ queryKey: authKeys.session() });
        if (!queryClient.getQueryData(authKeys.session())) {
          throw new Error('Session not established after 2FA.');
        }
        announceAuthChange();
        setIsTwoFactorRequired(false);
        setTwoFactorCode('');
        navigate('/user-home', { replace: true });
      }
    } catch (err) {
      // console.log('2FA verification error:', err);
      // console.log('2FA error response:', err.response);
      setError(err.response?.data?.message || 'Failed to verify two-factor authentication code.');
    } finally {
      setLoading(false);
    }
  };

  // Signing out is a local operation that happens to tell the server about
  // itself. The teardown below runs whether or not that call succeeds.
  //
  // It used to live in the try block, so a failed request left the tab holding
  // `user`, rendering authenticated routes, and — the part that matters — still
  // holding the query cache full of that user's reservations and billing data.
  // Someone who clicked "Log out" and got an error ribbon was looking at a
  // screen that said they were signed in, on what may well be a shared machine.
  //
  // There is no outcome where refusing to sign out locally is the safer
  // choice, and no useful "try again": the request is not what signs you out
  // of this browser. Errors are logged rather than shown, because a ribbon
  // saying "Logout Failed" over a login page is just wrong.
  const signOut = async () => {
    setLoading(true);
    try {
      await apiLogout();
    } catch (err) {
      // Kept out of the UI on purpose — see above. Worth a console line for
      // whoever debugs a server that stops accepting logouts.
      console.error('Logout request failed; signing out locally anyway', err);
    } finally {
      // Reset the 2FA flag along with the user. Without this reset, a
      // user who completed 2FA in this tab and then logged out would
      // land on /login with isTwoFactorRequired still true — SignInCard
      // renders the 6-digit-code form instead of the email/password
      // form, which reads as "app is broken" to anyone not carrying a
      // fresh 2FA code from thin air.
      setIsTwoFactorRequired(false);
      setTwoFactorCode('');
      setError(null);
      // Drop every server-state cache entry so User A's reservations
      // don't leak into User B's next session on the same browser tab.
      // Client state (SelectedRatesContext) is separate and cleared by
      // its own means; queryClient owns only server data.
      queryClient.clear();
      // Seed the emptied cache with the answer rather than letting the auth
      // query refetch its way there. Without it `checking` flips back to true
      // for a beat and the guards render nothing mid-navigation.
      queryClient.setQueryData(authKeys.session(), null);
      announceAuthChange();
      navigate('/login', { replace: true });
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      // state
      email, setEmail, password, setPassword, twoFactorCode, setTwoFactorCode,
      user, checking, error, loading, isTwoFactorRequired, setError,
      // actions
      signIn, submitTwoFactor, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
